/**
 * Upload manager for intake-session attachments.
 *
 * Holds the list the wizard renders: server-confirmed attachments ('done')
 * plus in-flight ('uploading') and failed ('error') local uploads. A failed
 * upload never blocks the wizard — it stays in the list with Retry / Remove.
 * `upload()` / `remove()` take the session id per call so the hook can be
 * driven from the streaming `session_started` event before React state has
 * caught up.
 */
import { useCallback, useRef, useState } from 'react';

import { ApiError, intake } from '@/helpers/ApiHelper';
import { isTransientUploadFailure } from '@/lib/attachments';
import type { IntakeAttachment } from '@/types/intake';

export type AttachmentItemStatus = 'queued' | 'uploading' | 'done' | 'error';

export interface AttachmentItem {
  /** Stable client key (the attachment id once uploaded). */
  key: string;
  filename: string;
  size_bytes: number;
  content_type: string;
  status: AttachmentItemStatus;
  /** 0-100 while uploading. */
  progress: number;
  error: string | null;
  /** Failed with a transient error (network / timeout / 5xx), so Retry can help. */
  retryable?: boolean;
  /** Set once the server has stored it. */
  attachment: IntakeAttachment | null;
}

export function itemFromAttachment(a: IntakeAttachment): AttachmentItem {
  return {
    key: a.id,
    filename: a.filename,
    size_bytes: a.size_bytes,
    content_type: a.content_type,
    status: 'done',
    progress: 100,
    error: null,
    attachment: a,
  };
}

export function itemFromFile(key: string, file: File, status: AttachmentItemStatus): AttachmentItem {
  return {
    key,
    filename: file.name,
    size_bytes: file.size,
    content_type: file.type,
    status,
    progress: 0,
    error: null,
    attachment: null,
  };
}

export interface UseAttachmentUploads {
  items: AttachmentItem[];
  /** Upload files to a session (sequentially). */
  upload: (sessionId: string, files: File[]) => Promise<void>;
  /** Re-run a failed upload. */
  retry: (sessionId: string, key: string) => Promise<void>;
  /** Delete a server attachment, or drop a failed local item. */
  remove: (sessionId: string, key: string) => Promise<void>;
  /** Merge the server's list (resume / reconcile); keeps in-flight + failed local items. */
  hydrate: (serverList: IntakeAttachment[]) => void;
  clear: () => void;
}

let keyCounter = 0;
const nextKey = (): string => `upload-${Date.now()}-${keyCounter++}`;

export function useAttachmentUploads(): UseAttachmentUploads {
  const [items, setItemsState] = useState<AttachmentItem[]>([]);
  // Source of truth for synchronous reads (remove) — React state lags a render behind.
  const itemsRef = useRef<AttachmentItem[]>([]);
  const setItems = useCallback((updater: (prev: AttachmentItem[]) => AttachmentItem[]) => {
    itemsRef.current = updater(itemsRef.current);
    setItemsState(itemsRef.current);
  }, []);
  const filesRef = useRef<Map<string, File>>(new Map());

  const patchItem = useCallback((key: string, patch: Partial<AttachmentItem>) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  }, [setItems]);

  const runUpload = useCallback(
    async (sessionId: string, key: string, file: File): Promise<void> => {
      patchItem(key, { status: 'uploading', progress: 0, error: null });
      try {
        const att = await intake.uploadAttachment(sessionId, file, (percent) =>
          patchItem(key, { progress: percent }),
        );
        filesRef.current.delete(key);
        setItems((prev) => prev.map((it) => (it.key === key ? itemFromAttachment(att) : it)));
      } catch (e) {
        patchItem(key, {
          status: 'error',
          error: e instanceof ApiError ? e.detail : 'Upload failed',
          retryable: e instanceof ApiError ? isTransientUploadFailure(e.status) : true,
        });
      }
    },
    [patchItem, setItems],
  );

  const upload = useCallback(
    async (sessionId: string, files: File[]): Promise<void> => {
      const fresh = files.map((file) => {
        const item = itemFromFile(nextKey(), file, 'uploading');
        filesRef.current.set(item.key, file);
        return { item, file };
      });
      setItems((prev) => [...prev, ...fresh.map(({ item }) => item)]);
      for (const { item, file } of fresh) await runUpload(sessionId, item.key, file);
    },
    [runUpload, setItems],
  );

  const retry = useCallback(
    async (sessionId: string, key: string): Promise<void> => {
      const file = filesRef.current.get(key);
      if (file) await runUpload(sessionId, key, file);
    },
    [runUpload],
  );

  const remove = useCallback(
    async (sessionId: string, key: string): Promise<void> => {
      const item = itemsRef.current.find((it) => it.key === key);
      if (!item) return;
      if (item.status === 'done' && item.attachment) {
        try {
          await intake.deleteAttachment(sessionId, item.attachment.id);
        } catch (e) {
          patchItem(key, { error: e instanceof ApiError ? e.detail : 'Could not remove file' });
          return;
        }
      }
      filesRef.current.delete(key);
      setItems((prev) => prev.filter((it) => it.key !== key));
    },
    [patchItem, setItems],
  );

  const hydrate = useCallback((serverList: IntakeAttachment[]) => {
    setItems((prev) => {
      const serverIds = new Set(serverList.map((a) => a.id));
      const local = prev.filter(
        (it) => it.status !== 'done' || (it.attachment !== null && !serverIds.has(it.attachment.id)),
      );
      return [...serverList.map(itemFromAttachment), ...local];
    });
  }, [setItems]);

  const clear = useCallback(() => {
    filesRef.current.clear();
    setItems(() => []);
  }, [setItems]);

  return { items, upload, retry, remove, hydrate, clear };
}
